import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  BscArea,
  BscAreaUsuario,
  BscAvanceCaptura,
  BscCiclo,
  BscCicloTrimestre,
  BscEjercicioKpi,
  BscImportacionPedido,
  BscImportacionPlan,
  BscReq,
  BscNorma,
  BscRes,
  CorteBsc,
  FiltroBandejaBsc,
} from "../shared/model/bsc/bsc.model";

/**
 * BSC (FOR-011): puerta única del módulo hacia el backend (docs/BSC.md §5 y §7).
 *
 * Un método por endpoint, todos POST de BscReq a BscRes. El usuario y la
 * empresa nunca viajan: el servidor los toma de la sesión. Los errores de
 * negocio llegan en un 200 con codigoError/mensajeError (AUTH-001 sin
 * sesión); quien llama los revisa, como en homologacion-modelos.
 *
 * F2 trae catálogos, parámetros (T4) y el cierre del año; F3 la carga del
 * FOR-011 (T1, multipart); F4 la lectura de A1/A2/A3 y la exportación del
 * FOR-011 (los dos GET de blob); F5 la planeación del año (T3); F6 la
 * captura y el cierre del trimestre (C1/C3) y la revisión de Dirección (C2);
 * F7 el comparativo anual (T2); los demás endpoints llegan con su pantalla.
 */
@Injectable({ providedIn: "root" })
export class BscService {
  constructor(private readonly http: HttpClient) {}

  indicadoresConsultar(anio: number, corte: CorteBsc, idNorma?: number): Observable<BscRes> {
    const req: BscReq = { anio, corte, idNorma };
    return this.http.post<BscRes>(UrlConstantes.BSC_INDICADORES_CONSULTAR, req);
  }

  indicadoresNorma(norma: BscNorma): Observable<BscRes> {
    return this.http.post<BscRes>(UrlConstantes.BSC_INDICADORES_NORMA, { norma } satisfies BscReq);
  }

  indicadoresLista(anio: number, idNorma: number, idKpis: number[]): Observable<BscRes> {
    return this.http.post<BscRes>(UrlConstantes.BSC_INDICADORES_LISTA, { anio, idNorma, idKpis } satisfies BscReq);
  }

  indicadoresSugerir(req: Pick<BscReq, "idEjercicioKpi" | "sugerido">): Observable<BscRes> {
    return this.http.post<BscRes>(UrlConstantes.BSC_INDICADORES_SUGERIR, req);
  }

  indicadoresExportar(anio: number, idNorma: number, corte: CorteBsc): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set("anio", anio).set("idNorma", idNorma).set("corte", corte);
    return this.http.get(UrlConstantes.BSC_INDICADORES_EXPORTAR, { params, responseType: "blob", observe: "response" });
  }

  /**
   * Años (con estatus y origen del ciclo), áreas activas con esMia,
   * unidades, año en curso y corte vigente, más las banderas puede*.
   * Basta la hoja raíz admin/bsc.
   */
  catalogos(): Observable<BscRes> {
    return this.http.post<BscRes>(UrlConstantes.BSC_CATALOGOS, {});
  }

  /* ---------- A1 · A2 · A3: lectura (F4, BscConsultaBuss) ----------
   * Basta la hoja raíz admin/bsc; cualquier perfil BSC abre el detalle o la
   * ficha de otra área de su empresa. Las banderas puede* vienen calculadas
   * para el área consultada. Un bloque que no se pudo leer llega vacío con
   * su texto en avisos (dashboard.avisos, areaDetalle.avisos o res.avisos).
   */

  /** A1: tiles, series, pastel y la tabla de áreas del año al corte (Q1..Q4 o ANUAL). */
  dashboard(anio: number, corte: CorteBsc): Observable<BscRes> {
    const req: BscReq = { anio, corte };
    return this.http.post<BscRes>(UrlConstantes.BSC_DASHBOARD, req);
  }

  /** A2: un área en el año al corte, con sus KPI, sus 4 trimestres y el año anterior al mismo corte. */
  areaDetalle(anio: number, idBscArea: number, corte: CorteBsc): Observable<BscRes> {
    const req: BscReq = { anio, idBscArea, corte };
    return this.http.post<BscRes>(UrlConstantes.BSC_AREA_DETALLE, req);
  }

  /**
   * A3: la ficha del KPI del año (kpiFicha, meses, historial y kpiAnterior).
   * El corte (el de A2) es el trimestre del tile del año anterior (F7.3);
   * nulo = el corte vigente de T2.
   */
  kpiFicha(idEjercicioKpi: number, corte: CorteBsc | null = null): Observable<BscRes> {
    const req: BscReq = { idEjercicioKpi };
    if (corte != null) {
      req.corte = corte;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_KPI_FICHA, req);
  }

  /**
   * T2 (F7.1, BscComparativoBuss): dos años de un área (o de toda la empresa,
   * sin idBscArea) KPI por KPI. mismoCorte true compara al mismo trimestre
   * (nulo = el corte vigente de B); false, el cierre anual de cada año. Exige
   * la hoja admin/bsc/comparativo; las banderas vienen para el área pedida.
   */
  comparativo(
    anioA: number | null,
    anioB: number | null,
    idBscArea: number | null,
    mismoCorte: boolean,
    trimestre: number | null,
  ): Observable<BscRes> {
    const req: BscReq = { mismoCorte };
    if (anioA != null) {
      req.anioA = anioA;
    }
    if (anioB != null) {
      req.anioB = anioB;
    }
    if (idBscArea != null) {
      req.idBscArea = idBscArea;
    }
    if (mismoCorte && trimestre != null) {
      req.trimestre = trimestre;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_COMPARATIVO, req);
  }

  /* ---------- Exportar el FOR-011 (F4.4, BscExcelBuss) ----------
   * GET con la respuesta completa (observe: "response") para leer el nombre
   * de Content-Disposition; el .xlsx llega como blob y un error como status
   * con su mensaje en texto plano (ver bsc-descarga.ts). Sin corte, el
   * servidor toma el último trimestre que ya salió de PENDIENTE. */

  /** A2: el FOR-011 de una planeación al corte (hoja BSC + Indicadores por Área). Basta la hoja raíz. */
  exportar(idEjercicio: number, corte: CorteBsc | null): Observable<HttpResponse<Blob>> {
    let params = new HttpParams().set("idEjercicio", `${idEjercicio}`);
    if (corte) {
      params = params.set("corte", corte);
    }
    return this.http.get(UrlConstantes.BSC_EXPORTAR, { params, observe: "response", responseType: "blob" });
  }

  /** A1: el FOR-011 consolidado del año (Indicadores por Área + una hoja BSC por área con planeación). */
  exportarConsolidado(anio: number, corte: CorteBsc | null): Observable<HttpResponse<Blob>> {
    let params = new HttpParams().set("anio", `${anio}`);
    if (corte) {
      params = params.set("corte", corte);
    }
    return this.http.get(UrlConstantes.BSC_EXPORTAR_CONSOLIDADO, { params, observe: "response", responseType: "blob" });
  }

  /**
   * T4: el ciclo del año con sus trimestres, áreas con usuarios, usuarios
   * elegibles, unidades, bloqueos y perfiles con llave. Si el año aún no
   * tiene ciclo, contesta el anterior más reciente (fechas pasadas al año
   * pedido) o los DEFAULT, con ciclo.heredado = true.
   */
  parametrosConsultar(anio: number): Observable<BscRes> {
    const req: BscReq = { anio };
    return this.http.post<BscRes>(UrlConstantes.BSC_PARAMETROS_CONSULTAR, req);
  }

  /** T4: crea el ciclo si no existe y guarda sus parámetros y las fechas de los 4 trimestres. */
  parametrosGuardar(ciclo: BscCiclo, trimestresCiclo: BscCicloTrimestre[]): Observable<BscRes> {
    const req: BscReq = { anio: ciclo.anio, ciclo, trimestresCiclo };
    return this.http.post<BscRes>(UrlConstantes.BSC_PARAMETROS_GUARDAR, req);
  }

  /**
   * T4: alta o edición de un área (con aliasExcel, área COS, vía y estatus)
   * y la lista COMPLETA de sus usuarios con rol: el servidor reemplaza la
   * lista del área. Contesta idGenerado y, si aplica, avisos (usuario sin
   * empleado, al que no le llegarán las notificaciones).
   */
  parametrosAreaGuardar(area: BscArea, usuarios: BscAreaUsuario[]): Observable<BscRes> {
    const req: BscReq = { area, usuarios };
    return this.http.post<BscRes>(UrlConstantes.BSC_PARAMETROS_AREA_GUARDAR, req);
  }

  /** T4 (F7.2): el maestro de KPIs del área (activos y retirados) con los años en que aparece cada uno. */
  parametrosKpis(idBscArea: number): Observable<BscRes> {
    const req: BscReq = { idBscArea };
    return this.http.post<BscRes>(UrlConstantes.BSC_PARAMETROS_KPIS, req);
  }

  /**
   * T4 (F7.2): fusiona el KPI origen en el destino: los años del origen pasan
   * al destino y el origen queda retirado. Contesta kpisMaestro del área ya
   * fusionada y un aviso; VAL-BSC-KPI-FUSION si no se puede (otra área,
   * retirado, los dos en un mismo año).
   */
  parametrosFusionarKpi(idKpiOrigen: number, idKpiDestino: number): Observable<BscRes> {
    const req: BscReq = { idKpiOrigen, idKpiDestino };
    return this.http.post<BscRes>(UrlConstantes.BSC_PARAMETROS_FUSIONAR_KPI, req);
  }

  /**
   * T4 (F7.2): recalcula con los parámetros guardados los snapshots de los
   * trimestres autorizados de un año importado del FOR-011. Contesta
   * trimestres (los recalculados), conteos y avisos (cada cambio).
   */
  parametrosRecalcular(anio: number): Observable<BscRes> {
    const req: BscReq = { anio };
    return this.http.post<BscRes>(UrlConstantes.BSC_PARAMETROS_RECALCULAR, req);
  }

  /** T4: cierra el año; exige Q4 AUTORIZADO en todos los ejercicios del ciclo. */
  cicloCerrar(anio: number): Observable<BscRes> {
    const req: BscReq = { anio };
    return this.http.post<BscRes>(UrlConstantes.BSC_CICLO_CERRAR, req);
  }

  /* ---------- T3 · Planeación de metas del año (F5, BscPlaneacionBuss) ----------
   * Los cinco contestan el MISMO estado de T3 del área y el año (ciclo con
   * sus fechas, la planeación con sus KPI revisados contra el año de origen,
   * bitácora, faltantes, avisos y puedeEditar) más las banderas de esa área
   * (puedePlanear, puedeAutorizar, tienePin): la pantalla pinta la respuesta
   * sin volver a consultar. Las metas solo se editan en BORRADOR/REGRESADA;
   * el servidor lo vuelve a exigir en cada escritura. */

  /** El estado de T3; sin planeación, el origen sugerido (la AUTORIZADA del año anterior del área). */
  planeacionConsultar(anio: number, idBscArea: number): Observable<BscRes> {
    const req: BscReq = { anio, idBscArea };
    return this.http.post<BscRes>(UrlConstantes.BSC_PLANEACION_CONSULTAR, req);
  }

  /**
   * Abre la planeación en BORRADOR: vacía, o copiada de la AUTORIZADA de
   * `anioOrigen` (KPI activos con meta, unidad, pesos, NA, descripción y
   * beneficios). Contesta el estado con idGenerado.
   */
  planeacionCrear(anio: number, idBscArea: number, anioOrigen: number | null): Observable<BscRes> {
    const req: BscReq = { anio, idBscArea };
    if (anioOrigen != null) {
      req.anioOrigen = anioOrigen;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_PLANEACION_CREAR, req);
  }

  /**
   * Guarda los KPI del borrador como los tiene la pantalla (idEjercicioKpi,
   * o idKpi del maestro, u objetivo de uno nuevo). Lo que no viene no se
   * toca; los textos nulos conservan lo guardado. No exige que se pueda
   * enviar: lo que falta sale en faltantes.
   */
  planeacionGuardar(idEjercicio: number, kpis: BscEjercicioKpi[]): Observable<BscRes> {
    const req: BscReq = { idEjercicio, kpis };
    return this.http.post<BscRes>(UrlConstantes.BSC_PLANEACION_GUARDAR, req);
  }

  /** BORRADOR/REGRESADA → ENVIADA; si algo lo impide, codigoError con los primeros problemas en palabras. */
  planeacionEnviar(idEjercicio: number): Observable<BscRes> {
    const req: BscReq = { idEjercicio };
    return this.http.post<BscRes>(UrlConstantes.BSC_PLANEACION_ENVIAR, req);
  }

  /**
   * Dirección: autoriza (`aprobar`) o regresa la planeación ENVIADA con su
   * PIN (llave admin/bsc/autorizar). Regresar exige el comentario. El PIN
   * solo viaja aquí; la pantalla no lo guarda.
   */
  planeacionResolver(idEjercicio: number, aprobar: boolean, comentario: string | null, pin: string): Observable<BscRes> {
    const req: BscReq = { idEjercicio, aprobar, pin };
    if (comentario) {
      req.comentario = comentario;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_PLANEACION_RESOLVER, req);
  }

  /* ---------- C1 · C3: captura y cierre del trimestre (F6, BscCapturaBuss) ----------
   * La captura manda SOLO idEjercicioKpi: planeación, área y empresa las
   * resuelve el servidor. mes.do y real.do piden la hoja admin/bsc/mi-bsc y un
   * área con vía GUIADO o AMBAS; previaCierre y cerrar, mi-bsc o tablero (y no
   * miran la vía: las dos vías terminan en el mismo cierre). Un trimestre
   * ENVIADO o AUTORIZADO ya no se captura (VAL-BSC-CONFLICTO); uno PENDIENTE sí.
   * Mi BSC, la previa y cerrar traen las banderas del área (puedeCapturar,
   * puedeCerrar); mes.do y real.do no las recalculan. */

  /**
   * C1: las áreas en que captura quien consulta con sus planeaciones del año
   * (null = el en curso) y, de la elegida (`idBscArea`, o la primera
   * autorizada), trimestres, fechas del ciclo, checklist (kpis, capturas y
   * los 12 meses de cada KPI con su estado), `hitos` (F6.3: la captura de
   * cada mes y las fechas de cada trimestre con su estado) y bitácora.
   */
  miBsc(anio: number | null, idBscArea: number | null): Observable<BscRes> {
    const req: BscReq = {};
    if (anio != null) {
      req.anio = anio;
    }
    if (idBscArea != null) {
      req.idBscArea = idBscArea;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_MI_BSC, req);
  }

  /**
   * C1: actividad y evidencia de un KPI en un mes calendario (1..12). null
   * conserva lo guardado y "" lo borra; al menos uno trae texto. Contesta
   * kpiMes (CAPTURADO) y avisos (KPI_NO_APLICA si el trimestre del KPI es NA).
   */
  capturarMes(idEjercicioKpi: number, mes: number, actividad: string | null, evidencia: string | null): Observable<BscRes> {
    const req: BscReq = { idEjercicioKpi, mes };
    if (actividad != null) {
      req.actividad = actividad;
    }
    if (evidencia != null) {
      req.evidencia = evidencia;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_MES_CAPTURAR, req);
  }

  /**
   * C3: el avance de un KPI en un trimestre (ver BscAvanceCaptura). Contesta
   * kpiTrimestre con K, L, N y semáforo, ejercicioTrimestre con el alcance del
   * área recalculado y los avisos de la normalización.
   */
  capturarReal(idEjercicioKpi: number, trimestre: number, avance: BscAvanceCaptura): Observable<BscRes> {
    const req: BscReq = { idEjercicioKpi, trimestre };
    if (avance.avanceReal != null) {
      req.avanceReal = avance.avanceReal;
    }
    if (avance.avanceProgramado != null) {
      req.avanceProgramado = avance.avanceProgramado;
    }
    if (avance.valorUnidades != null) {
      req.valorUnidades = avance.valorUnidades;
    }
    if (avance.recursos != null) {
      req.recursos = avance.recursos;
    }
    if (avance.comentario != null) {
      req.comentario = avance.comentario;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_REAL_CAPTURAR, req);
  }

  /**
   * C3 (y "Enviar Qn" de A2): lo que se enviaría a revisión, SIN escribir:
   * el trimestre con su alcance, kpis, capturas y meses del trimestre,
   * faltantes (vacío = se puede enviar) y kpisBajoMeta (si hay, enviar pide
   * comentario).
   */
  previaCierre(idEjercicio: number, trimestre: number): Observable<BscRes> {
    const req: BscReq = { idEjercicio, trimestre };
    return this.http.post<BscRes>(UrlConstantes.BSC_TRIMESTRE_PREVIA_CIERRE, req);
  }

  /**
   * EN_CAPTURA o REGRESADO → ENVIADO con el comentario para Dirección
   * (obligatorio si hay KPI bajo meta; hasta 1000). Contesta el estado de la
   * previa ya enviado; si algo lo impide, VAL-BSC-TRIMESTRE o VAL-BSC-FECHAS
   * con los primeros problemas en mensajeError.
   */
  cerrarTrimestre(idEjercicio: number, trimestre: number, comentario: string | null): Observable<BscRes> {
    const req: BscReq = { idEjercicio, trimestre };
    if (comentario) {
      req.comentario = comentario;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_TRIMESTRE_CERRAR, req);
  }

  /* ---------- C2 · Bandeja de revisión (F6.2, BscRevisionBuss) ----------
   * Solo idEjercicioTrimestre: planeación, área y empresa las resuelve el
   * servidor. bandeja, detalle y recordar piden la hoja admin/bsc/revision;
   * resolver y reabrir, la llave admin/bsc/autorizar y el PIN (como
   * planeacionResolver: el PIN solo viaja aquí). Nadie autoriza lo suyo: quien
   * lo envió o está asignado al área recibe VAL-BSC-PROPIA antes de que se le
   * pida el PIN (esPropio del renglón lo anticipa). bandeja, detalle,
   * resolver y reabrir traen las banderas (puedeAutorizar, tienePin). */

  /**
   * Un renglón por área con planeación autorizada en el trimestre del año
   * (null = el año en curso / el trimestre que toca revisar; res.trimestre
   * dice cuál), filtrado por `estatus` (null = TODOS), con conteos por filtro.
   */
  revisionBandeja(anio: number | null, trimestre: number | null, estatus: FiltroBandejaBsc | null): Observable<BscRes> {
    const req: BscReq = {};
    if (anio != null) {
      req.anio = anio;
    }
    if (trimestre != null) {
      req.trimestre = trimestre;
    }
    if (estatus != null) {
      req.estatus = estatus;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_REVISION_BANDEJA, req);
  }

  /**
   * El envío de un área: ejercicioTrimestre (alcance y esPropio), ejercicio,
   * sus 4 trimestres, kpis, capturas y meses del trimestre, kpisBajoMeta y la
   * bitácora del trimestre (historial).
   */
  revisionDetalle(idEjercicioTrimestre: number): Observable<BscRes> {
    const req: BscReq = { idEjercicioTrimestre };
    return this.http.post<BscRes>(UrlConstantes.BSC_REVISION_DETALLE, req);
  }

  /**
   * Dirección: autoriza (`aprobar`: congela el alcance y abre el trimestre
   * siguiente) o regresa el trimestre ENVIADO con su PIN. Regresar exige el
   * comentario (hasta 1000). Contesta el detalle ya resuelto.
   */
  revisionResolver(idEjercicioTrimestre: number, aprobar: boolean, comentario: string | null, pin: string): Observable<BscRes> {
    const req: BscReq = { idEjercicioTrimestre, aprobar, pin };
    if (comentario) {
      req.comentario = comentario;
    }
    return this.http.post<BscRes>(UrlConstantes.BSC_REVISION_RESOLVER, req);
  }

  /**
   * Dirección: AUTORIZADO → EN_CAPTURA con el motivo (obligatorio) y el PIN;
   * solo si el trimestre siguiente no está enviado ni autorizado. Anula el
   * alcance congelado. Contesta el detalle ya reabierto.
   */
  revisionReabrir(idEjercicioTrimestre: number, motivo: string, pin: string): Observable<BscRes> {
    const req: BscReq = { idEjercicioTrimestre, comentario: motivo, pin };
    return this.http.post<BscRes>(UrlConstantes.BSC_REVISION_REABRIR, req);
  }

  /**
   * "Recordar a pendientes": avisa a los responsables de cada área cuyo
   * trimestre sigue sin enviarse. enviados = a cuántas áreas; bandeja = esas;
   * avisos = las que no tenían a quién avisar o fallaron.
   */
  revisionRecordar(anio: number, trimestre: number): Observable<BscRes> {
    const req: BscReq = { anio, trimestre };
    return this.http.post<BscRes>(UrlConstantes.BSC_REVISION_RECORDAR, req);
  }

  /* ---------- T1 · Carga del FOR-011 (docs/BSC.md §6) ----------
   * Dos pasos con huella, calco de vacaciones.service: la previa NO escribe
   * nada; la carga manda OTRA VEZ el mismo archivo con la huella de la
   * previa y el servidor lo vuelve a leer y lo aplica todo o nada. Las dos
   * contestan el plan (BscImportacionPlan) con codigoError/mensajeError
   * adentro cuando no se pudo ni armar. */

  /** Lee el FOR-011 y devuelve lo que se cargaría. No escribe nada. */
  importarPrevia(archivo: File, pedido: BscImportacionPedido): Observable<BscImportacionPlan> {
    return this.http.post<BscImportacionPlan>(
      UrlConstantes.BSC_IMPORTAR_PREVIA,
      this.formaImportacion(archivo, pedido),
    );
  }

  /**
   * Aplica el plan revisado. `pedido` tiene que ser el MISMO de la previa
   * (entra en la huella); `filas` son las filas del Excel palomeadas: las
   * demás vuelven como OMITIDA y no se escriben.
   */
  importarCargar(
    archivo: File,
    pedido: BscImportacionPedido,
    huella: string,
    filas: number[],
  ): Observable<BscImportacionPlan> {
    return this.http.post<BscImportacionPlan>(
      UrlConstantes.BSC_IMPORTAR_CARGAR,
      this.formaImportacion(archivo, pedido, huella, filas),
    );
  }

  /**
   * El multipart de las dos llamadas, con los nombres de @RequestParam de
   * BscArchivoService. Sin Content-Type a mano: si se pone, el navegador ya
   * no calcula el boundary y el servidor no encuentra el archivo. Lo nulo
   * no viaja (para el servidor ausente = "el del libro" o "todas").
   */
  private formaImportacion(
    archivo: File,
    pedido: BscImportacionPedido,
    huella?: string,
    filas?: number[],
  ): FormData {
    const f = new FormData();
    f.append("file", archivo, archivo.name);
    f.append("modo", pedido.modo);
    const opcionales: [string, string | number | null | undefined][] = [
      ["anio", pedido.anio],
      ["hoja", pedido.hoja],
      ["idBscArea", pedido.idBscArea],
      ["hastaTrimestre", pedido.hastaTrimestre],
      ["asignaciones", pedido.asignaciones],
    ];
    for (const [nombre, valor] of opcionales) {
      if (valor != null && `${valor}` !== "") {
        f.append(nombre, `${valor}`);
      }
    }
    // En la carga la huella se manda aunque venga vacía: así el servidor
    // contesta "no llegó la huella" en vez de cargar sin ella.
    if (huella != null) {
      f.append("huella", huella);
    }
    if (filas && filas.length > 0) {
      f.append("filas", filas.join(","));
    }
    return f;
  }
}
